import { z } from "zod";

/**
 * Contratto dell'endpoint di health, condiviso tra API e web.
 * L'API valida la risposta prima di spedirla, il web la valida dopo averla
 * ricevuta: se il contratto cambia da un lato, l'altro se ne accorge subito.
 */

/**
 * - `ok`       tutto funziona
 * - `degraded` il servizio risponde ma un componente non essenziale è in difficoltà
 * - `down`     un componente essenziale non risponde
 */
export const healthStatusSchema = z.enum(["ok", "degraded", "down"]);
export type HealthStatus = z.infer<typeof healthStatusSchema>;

export const healthCheckSchema = z.object({
  /** Esito del singolo componente. */
  status: healthStatusSchema,
  /** Tempo di risposta del controllo in millisecondi, `null` se non misurabile. */
  latencyMs: z.number().nonnegative().nullable(),
  /** Dettaglio leggibile: versione del componente, causa dell'errore, ecc. */
  detail: z.string().nullable(),
});
export type HealthCheck = z.infer<typeof healthCheckSchema>;

export const healthResponseSchema = z.object({
  /** Il peggiore fra gli stati dei singoli controlli. */
  status: healthStatusSchema,
  service: z.string(),
  version: z.string(),
  environment: z.string(),
  /** Da quanto tempo il processo è vivo, in secondi. */
  uptimeSeconds: z.number().nonnegative(),
  /** Istante della rilevazione, ISO 8601. */
  timestamp: z.string(),
  checks: z.object({
    api: healthCheckSchema,
    database: healthCheckSchema,
  }),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;

/** Risposta della sonda di liveness: non tocca le dipendenze esterne. */
export const livenessResponseSchema = z.object({
  status: z.literal("ok"),
  uptimeSeconds: z.number().nonnegative(),
  timestamp: z.string(),
});
export type LivenessResponse = z.infer<typeof livenessResponseSchema>;

/** Ordine di gravità: serve a ridurre più controlli a un unico stato complessivo. */
const SEVERITY: Record<HealthStatus, number> = { ok: 0, degraded: 1, down: 2 };

/** Riduce N esiti al peggiore fra loro. Senza controlli, l'esito è `ok`. */
export function worstStatus(statuses: readonly HealthStatus[]): HealthStatus {
  return statuses.reduce<HealthStatus>(
    (worst, current) => (SEVERITY[current] > SEVERITY[worst] ? current : worst),
    "ok",
  );
}
